/**
 * Admin Gender Party Edit Page
 * Create/edit gender party with state machine controls
 */

import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/layout";
import { PageLoader } from "@/components/PageLoader";
import { config } from "@/config";
import {
  IconAlertTriangle,
  IconSave,
  IconTrash,
  IconPlay,
  IconPause,
  IconEye,
  IconRefresh,
  IconExternalLink,
  IconPlus,
  IconPhoto,
  IconCalendar,
  IconX,
  IconChevronDown,
} from "@/components/Icons";
import { getTelegram } from "@/lib/telegram";
import {
  useAdminGenderParty,
  useAdminGenderPartyEvents,
  useGenderPartyMutations,
  useGenderPartyPhotoUpload,
  useCouplePhotoUpload,
  type GenderPartyInput,
  type GenderVote,
  type ShowResultsMode,
  type GenderPartyEventSummary,
} from "./hooks";

export function AdminGenderPartyEditPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tg = getTelegram();
  const isNew = id === "new" || !id;
  const preselectedEventId = searchParams.get("eventId");

  const { genderParty, isLoading, error, refetch } = useAdminGenderParty(
    isNew ? undefined : id
  );
  const { events } = useAdminGenderPartyEvents();
  const mutations = useGenderPartyMutations();

  const [formData, setFormData] = useState<GenderPartyInput>({
    name: "",
    description: "",
    eventId: null,
    partnerOneName: "",
    partnerTwoName: "",
    couplePhotoUrl: "",
    allowVoteChange: true,
    showResultsMode: "AFTER_VOTE",
    answer: null,
    isTest: false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);

  const {
    isUploading: isUploadingPhoto,
    uploadProgress,
    fileInputRef,
    uploadPhotos,
    triggerFileSelect,
  } = useGenderPartyPhotoUpload({
    genderPartyId: isNew ? undefined : id,
    onBatchComplete: () => {
      refetch();
    },
  });

  const {
    isUploading: isUploadingCouplePhoto,
    fileInputRef: couplePhotoInputRef,
    uploadCouplePhoto,
    triggerFileSelect: triggerCouplePhotoSelect,
  } = useCouplePhotoUpload({
    genderPartyId: isNew ? undefined : id,
    onSuccess: () => {
      refetch();
    },
  });

  useEffect(() => {
    if (genderParty) {
      setFormData({
        name: genderParty.name,
        description: genderParty.description ?? "",
        eventId: genderParty.eventId,
        partnerOneName: genderParty.partnerOneName,
        partnerTwoName: genderParty.partnerTwoName,
        couplePhotoUrl: genderParty.couplePhotoUrl ?? "",
        allowVoteChange: genderParty.allowVoteChange,
        showResultsMode: genderParty.showResultsMode,
        answer: genderParty.answer,
        isTest: genderParty.isTest,
      });
    } else if (isNew && preselectedEventId) {
      setFormData((prev) => ({ ...prev, eventId: preselectedEventId }));
    }
  }, [genderParty, isNew, preselectedEventId]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (preselectedEventId) {
        navigate(`/admin/gender-party?eventId=${preselectedEventId}`);
      } else if (genderParty?.eventId) {
        navigate(`/admin/gender-party?eventId=${genderParty.eventId}`);
      } else {
        navigate("/admin/gender-party");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, preselectedEventId, genderParty?.eventId]);

  const validateForm = useCallback((): string | null => {
    if (!formData.name.trim()) {
      return "Название обязательно";
    }
    if (!formData.partnerOneName.trim()) {
      return "Имя партнёра 1 обязательно";
    }
    if (!formData.partnerTwoName.trim()) {
      return "Имя партнёра 2 обязательно";
    }
    return null;
  }, [formData]);

  const handleSave = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      if (isNew) {
        const result = await mutations.create(formData);
        navigate(`/admin/gender-party/${result.id}`, { replace: true });
      } else {
        await mutations.update(id!, formData);
        refetch();
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Ошибка сохранения");
    } finally {
      setIsSaving(false);
    }
  }, [isNew, id, formData, mutations, navigate, refetch, validateForm]);

  const handleDelete = useCallback(async () => {
    if (!confirm("Удалить голосование?")) return;
    try {
      await mutations.remove(id!);
      navigate("/admin/gender-party");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Ошибка удаления");
    }
  }, [id, mutations, navigate]);

  const handleAction = useCallback(
    async (action: () => Promise<unknown>, successMsg?: string) => {
      setActionError(null);
      try {
        await action();
        refetch();
        if (successMsg && tg) {
          tg.showAlert(successMsg);
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Ошибка");
      }
    },
    [refetch, tg]
  );

  const handleActionWithConfirm = useCallback(
    async (
      confirmMsg: string,
      action: () => Promise<unknown>,
      successMsg?: string
    ) => {
      if (!confirm(confirmMsg)) return;
      await handleAction(action, successMsg);
    },
    [handleAction]
  );

  const handlePhotoFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (!fileList || fileList.length === 0) return;
      setPhotoError(null);
      await uploadPhotos(Array.from(fileList));
    },
    [uploadPhotos]
  );

  const handleCouplePhotoFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      await uploadCouplePhoto(file);
    },
    [uploadCouplePhoto]
  );

  const handleDeletePhoto = useCallback(async (photoId: string) => {
    if (!confirm("Удалить фото?")) return;
    setPhotoError(null);
    try {
      await mutations.deletePhoto(photoId);
      refetch();
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : "Ошибка удаления фото");
    }
  }, [mutations, refetch]);

  if (isLoading && !isNew) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  if (error && !isNew) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <IconAlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-slate-600">Ошибка загрузки</p>
            <p className="text-sm text-slate-500 mt-1">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const status = genderParty?.status ?? "DRAFT";
  const canEdit = status === "DRAFT" || status === "VOTING_CLOSED";

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-slate-900">
              {isNew ? "Новое голосование" : genderParty?.name}
            </h1>
            {!isNew && genderParty?.isTest && (
              <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
                ТЕСТ
              </span>
            )}
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Status and Actions */}
          {!isNew && genderParty && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-sm text-slate-500">Статус</span>
                  <p className="font-semibold text-slate-900">
                    {status === "DRAFT" && "Черновик"}
                    {status === "VOTING_OPEN" && "Голосование открыто"}
                    {status === "VOTING_CLOSED" && "Голосование закрыто"}
                    {status === "REVEALED" && "Результаты показаны"}
                    {status === "COMPLETED" && "Завершено"}
                  </p>
                </div>

                {genderParty.voteCounts && (
                  <div className="text-right">
                    <span className="text-sm text-slate-500">Голосов</span>
                    <p className="font-semibold text-slate-900">
                      {genderParty.voteCounts.totalVotes}
                    </p>
                  </div>
                )}
              </div>

              {actionError && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {actionError}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {status === "DRAFT" && (
                  <button
                    onClick={() =>
                      handleActionWithConfirm(
                        "Открыть голосование? Пользователи смогут голосовать.",
                        () => mutations.openVoting(id!),
                        "Голосование открыто"
                      )
                    }
                    className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600"
                  >
                    <IconPlay className="w-4 h-4" />
                    Открыть голосование
                  </button>
                )}

                {status === "VOTING_OPEN" && (
                  <button
                    onClick={() =>
                      handleActionWithConfirm(
                        "Закрыть голосование? Пользователи больше не смогут голосовать.",
                        () => mutations.closeVoting(id!),
                        "Голосование закрыто"
                      )
                    }
                    className="flex items-center gap-1 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
                  >
                    <IconPause className="w-4 h-4" />
                    Закрыть голосование
                  </button>
                )}

                {status === "VOTING_CLOSED" && (
                  <>
                    <button
                      onClick={() =>
                        handleActionWithConfirm(
                          "Открыть голосование снова?",
                          () => mutations.openVoting(id!),
                          "Голосование открыто"
                        )
                      }
                      className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600"
                    >
                      <IconPlay className="w-4 h-4" />
                      Открыть снова
                    </button>
                    <button
                      onClick={() =>
                        handleActionWithConfirm(
                          "Показать результаты голосования всем участникам?",
                          () => mutations.revealResults(id!),
                          "Результаты показаны"
                        )
                      }
                      className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600"
                    >
                      <IconEye className="w-4 h-4" />
                      Показать результаты
                    </button>
                  </>
                )}

                {status === "REVEALED" && genderParty.answer && (
                  <button
                    onClick={() =>
                      handleActionWithConfirm(
                        `Раскрыть ответ: ${genderParty.answer === "BOY" ? "мальчик" : "девочка"}?`,
                        () => mutations.revealAnswer(id!),
                        "Ответ раскрыт"
                      )
                    }
                    className="flex items-center gap-1 px-3 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600"
                  >
                    {genderParty.answer === "BOY" ? "👦" : "👧"} Раскрыть ответ
                  </button>
                )}

                {genderParty.isTest && status !== "DRAFT" && (
                  <button
                    onClick={() =>
                      handleActionWithConfirm(
                        "Сбросить тестовое голосование? Все голоса будут удалены.",
                        () => mutations.resetTest(id!),
                        "Голосование сброшено"
                      )
                    }
                    className="flex items-center gap-1 px-3 py-2 bg-slate-500 text-white rounded-lg text-sm font-medium hover:bg-slate-600"
                  >
                    <IconRefresh className="w-4 h-4" />
                    Сбросить тест
                  </button>
                )}
              </div>

              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <a
                  href={config.genderPartyDeepLink(id!)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
                >
                  <IconExternalLink className="w-4 h-4" />
                  Голосование
                </a>
                <a
                  href={`${window.location.origin}/gender-party/${id}/screen`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
                >
                  <IconExternalLink className="w-4 h-4" />
                  Экран
                </a>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
            <h2 className="font-semibold text-slate-900">Основное</h2>

            {saveError && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {saveError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Название
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, name: e.target.value }))
                }
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                placeholder="Baby Shower Ивановых"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Партнёр 1
                </label>
                <input
                  type="text"
                  value={formData.partnerOneName}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, partnerOneName: e.target.value }))
                  }
                  disabled={!canEdit}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  placeholder="Иван"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Партнёр 2
                </label>
                <input
                  type="text"
                  value={formData.partnerTwoName}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, partnerTwoName: e.target.value }))
                  }
                  disabled={!canEdit}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  placeholder="Мария"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Описание
              </label>
              <textarea
                value={formData.description ?? ""}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, description: e.target.value }))
                }
                disabled={!canEdit}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                placeholder="Описание события..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Мероприятие
              </label>
              <EventSelector
                events={events}
                selectedEventId={formData.eventId}
                onSelect={(eventId) =>
                  setFormData((f) => ({ ...f, eventId }))
                }
                disabled={!canEdit}
                isOpen={isEventDropdownOpen}
                onToggle={() => setIsEventDropdownOpen(!isEventDropdownOpen)}
                onClose={() => setIsEventDropdownOpen(false)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Привязать к мероприятию (опционально)
              </p>
            </div>

            {/* Couple Photo Upload - only for existing gender party */}
            {!isNew && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Фото пары
                </label>
                {genderParty?.couplePhotoUrl && (
                  <div className="mb-3">
                    <img
                      src={genderParty.couplePhotoUrl}
                      alt="Фото пары"
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                  </div>
                )}
                <input
                  ref={couplePhotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleCouplePhotoFileSelect}
                  className="hidden"
                />
                {canEdit && (
                  <button
                    type="button"
                    onClick={triggerCouplePhotoSelect}
                    disabled={isUploadingCouplePhoto}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <IconPhoto className="w-4 h-4" />
                    {isUploadingCouplePhoto
                      ? "Загрузка..."
                      : genderParty?.couplePhotoUrl
                        ? "Заменить фото"
                        : "Загрузить фото"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Photo Carousel Management */}
          {!isNew && genderParty && (
            <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <IconPhoto className="w-5 h-5 text-slate-500" />
                  Фото карусели
                  {genderParty.photos.length > 0 && (
                    <span className="text-xs text-slate-400 font-normal">
                      {genderParty.photos.length}
                    </span>
                  )}
                </h2>
                {canEdit && (
                  <button
                    type="button"
                    onClick={triggerFileSelect}
                    disabled={isUploadingPhoto}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium active:bg-blue-100 disabled:opacity-50"
                  >
                    <IconPlus className="w-4 h-4" />
                    Добавить
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handlePhotoFileSelect}
                className="hidden"
              />

              {photoError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {photoError}
                </div>
              )}

              {/* Upload progress */}
              {isUploadingPhoto && uploadProgress && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin shrink-0" />
                  <span className="text-sm text-blue-700">
                    Загрузка {uploadProgress.current} из {uploadProgress.total}…
                  </span>
                </div>
              )}

              {/* Photo grid */}
              {genderParty.photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {genderParty.photos
                    .slice()
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((photo, index) => (
                      <div key={photo.id} className="relative group">
                        <img
                          src={photo.imageUrl}
                          alt={photo.caption ?? `Фото ${index + 1}`}
                          className="w-full aspect-square object-cover rounded-lg"
                        />
                        <span className="absolute top-1 left-1 min-w-[20px] h-5 flex items-center justify-center text-[11px] font-medium text-white bg-black/50 rounded-full px-1">
                          {index + 1}
                        </span>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500/80 text-white rounded-full active:bg-red-600"
                          >
                            <IconTrash className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              ) : !isUploadingPhoto ? (
                <button
                  type="button"
                  onClick={canEdit ? triggerFileSelect : undefined}
                  disabled={!canEdit}
                  className="w-full py-8 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-2 text-slate-400 active:border-slate-300 transition-colors disabled:opacity-60"
                >
                  <IconPhoto className="w-8 h-8" />
                  <span className="text-sm">Добавить фото в карусель</span>
                </button>
              ) : null}
            </div>
          )}

          {/* Settings */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
            <h2 className="font-semibold text-slate-900">Настройки</h2>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Правильный ответ
              </label>
              <select
                value={formData.answer ?? ""}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    answer: (e.target.value || null) as GenderVote | null,
                  }))
                }
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
              >
                <option value="">Не задан</option>
                <option value="BOY">👦 Мальчик</option>
                <option value="GIRL">👧 Девочка</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Показывать результаты
              </label>
              <select
                value={formData.showResultsMode}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    showResultsMode: e.target.value as ShowResultsMode,
                  }))
                }
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
              >
                <option value="ALWAYS">Всегда</option>
                <option value="AFTER_VOTE">После голосования</option>
                <option value="ON_REVEAL">После раскрытия</option>
              </select>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.allowVoteChange}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, allowVoteChange: e.target.checked }))
                }
                disabled={!canEdit}
                className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-sm text-slate-700">
                Разрешить менять голос
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isTest}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, isTest: e.target.checked }))
                }
                disabled={!canEdit}
                className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-sm text-slate-700">
                Тестовый режим (можно сбросить)
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving || !canEdit}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <IconSave className="w-5 h-5" />
              {isSaving ? "Сохранение..." : "Сохранить"}
            </button>

            {!isNew && status === "DRAFT" && (
              <button
                onClick={handleDelete}
                className="px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600"
              >
                <IconTrash className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// =============================================================================
// Event Selector Component
// =============================================================================

function formatEventDate(dateString: string | null): string {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface EventSelectorProps {
  events: GenderPartyEventSummary[];
  selectedEventId: string | null | undefined;
  onSelect: (eventId: string | null) => void;
  disabled?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

function EventSelector({
  events,
  selectedEventId,
  onSelect,
  disabled,
  isOpen,
  onToggle,
  onClose,
}: EventSelectorProps) {
  const selectedEvent = events.find((e) => e.id === selectedEventId);

  const handleSelect = (eventId: string | null) => {
    onSelect(eventId);
    onClose();
  };

  return (
    <div className="relative">
      {/* Selected event display / trigger button */}
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={`w-full text-left px-3 py-2 border rounded-lg transition-colors ${
          disabled
            ? "bg-slate-100 border-slate-200 cursor-not-allowed"
            : "bg-white border-slate-200 hover:border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        }`}
      >
        {selectedEvent ? (
          <div className="flex items-center gap-3">
            {selectedEvent.coverImage ? (
              <img
                src={selectedEvent.coverImage}
                alt=""
                className="w-10 h-10 rounded-lg object-cover shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <IconCalendar className="w-5 h-5 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">
                {selectedEvent.title}
              </div>
              {selectedEvent.startDate && (
                <div className="text-xs text-slate-500">
                  {formatEventDate(selectedEvent.startDate)}
                </div>
              )}
            </div>
            <IconChevronDown
              className={`w-5 h-5 text-slate-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        ) : (
          <div className="flex items-center justify-between text-slate-500">
            <span>Без мероприятия</span>
            <IconChevronDown
              className={`w-5 h-5 text-slate-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        )}
      </button>

      {/* Clear button for selected event */}
      {selectedEvent && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSelect(null);
          }}
          className="absolute right-10 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
          title="Убрать мероприятие"
        >
          <IconX className="w-4 h-4" />
        </button>
      )}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={onClose} />

          {/* Dropdown content */}
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
            {/* Without event option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors ${
                !selectedEventId ? "bg-blue-50" : ""
              }`}
            >
              <span className="text-slate-500">Без мероприятия</span>
            </button>

            {/* Divider */}
            {events.length > 0 && <div className="border-t border-slate-100" />}

            {/* Events list */}
            {events.map((event) => (
              <button
                key={event.id}
                type="button"
                onClick={() => handleSelect(event.id)}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors ${
                  selectedEventId === event.id ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  {event.coverImage ? (
                    <img
                      src={event.coverImage}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <IconCalendar className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {event.title}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {event.startDate && (
                        <span>{formatEventDate(event.startDate)}</span>
                      )}
                      {event.genderPartiesCount > 0 && (
                        <span className="text-pink-500">
                          {event.genderPartiesCount} голосовани
                          {event.genderPartiesCount === 1
                            ? "е"
                            : event.genderPartiesCount < 5
                            ? "я"
                            : "й"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}

            {events.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                Нет доступных мероприятий
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
